import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from "../UserContext";
import axios from 'axios';
import Header from '../Header'; // assuming you have a shared Header component
import './index.css';   // style this file for table responsiveness

const CustomersList = () => {
  const { user, token } = useContext(UserContext);
  const [customers, setCustomers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('all');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'active' | 'inactive'
  const [sortByExpiryAsc, setSortByExpiryAsc] = useState(false);

  const navigate = useNavigate();
  
  const role = user.role;

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setShowLoading(true);
      const response = await axios.get(`https://gsrtv.in/portal_php/allCustomers.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });      
      setCustomers(response.data || []);
      setFilteredCustomers(response.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
      setShowLoading(false);
    }
  };

  const fetchOperators = async () => {
      try {
        const response = await fetch(`https://gsrtv.in/portal_php/operator_api.php`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        const data = await response.json();        
        if (response.ok) {
          setOperators(data.operators);
          setError(null);
        } else {
          setError(data.error || 'Failed to fetch operators');
        }
      } catch (err) {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };
  

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (role !== "operator") {
      fetchOperators();
    }
  }, [role]);

  // Debounced Search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (!searchTerm) {
        setFilteredCustomers(customers);
        return;
      }
      const term = searchTerm.toLowerCase();
      const results = customers.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(term)) ||
          (c.MobileNumber && c.MobileNumber.includes(term)) ||
          (c.username && c.username.toLowerCase().includes(term))
      );
      setFilteredCustomers(results);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, customers]);

  const isActive = (expiryDate) => {
    return new Date(expiryDate) >= new Date();
  };

  useEffect(() => {
    let list = [...customers];

    if (filterStatus === 'active') {
      list = list.filter(c => isActive(c.ExpiryDate));
    } else if (filterStatus === 'inactive') {
      list = list.filter(c => !isActive(c.ExpiryDate));
    }
    if (selectedOperatorId !== 'all') {
      list = list.filter(c => c.operatorId === selectedOperatorId);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(term)) ||
          (c.MobileNumber && c.MobileNumber.includes(term)) ||
          (c.username && c.username.toLowerCase().includes(term))
      );
    }
    
    setFilteredCustomers(list);
  }, [searchTerm, customers, filterStatus, selectedOperatorId]);



  useEffect(() => {
    let list = [...filteredCustomers];
    // ✅ Sort by ExpiryDate
    list.sort((a, b) => {
      const dateA = new Date(a.ExpiryDate);
      const dateB = new Date(b.ExpiryDate);
      return sortByExpiryAsc ? dateA - dateB : dateB - dateA;
    });

    setFilteredCustomers(list);
  }, [sortByExpiryAsc]);

  const highlightText = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };


  return (
    <>
      <Header />

      <div className="operator-header">
        <h2>Customers</h2>
        {role !== "operator" ? (
          <select
            value={selectedOperatorId}
            onChange={(e) => setSelectedOperatorId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="search-input"
          >
            <option value="all">All Operators</option>
            {/* Admin himself only when role=admin */}
            {role === "admin" && (
              <option
                key={`admin-${user.operatorId}`}
                value={user.operatorId}
              >
                {user.operatorName} - {user.operatorPhoneNumber} (Admin)
              </option>
            )}
            
            {operators.map(op => (
              <option key={op.id} value={op.id}>
                {op.operatorName}
              </option>
            ))}
          </select>
          ) : (
          ""
        )}

        <input
          type="text"
          className="search-input"
          placeholder="Search by name or phone"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="add-operator-btn" onClick={() => navigate('/add-customer')}>
          + Add Customer
        </button>
      </div>


      <div className="customer-summary">
        <div className="customer-summary">
          <strong>Total: {customers.length}</strong> |{" "}
          <span style={{ color: 'green' }}>Active: {customers.filter(c => isActive(c.ExpiryDate)).length}</span> |{" "}
          <span style={{ color: 'red' }}>Inactive: {customers.filter(c => !isActive(c.ExpiryDate)).length}</span>
        </div>

        <div className="status-filters">
          <button onClick={() => setFilterStatus('all')} className={filterStatus === 'all' ? 'active' : ''}>All</button>
          <button onClick={() => setFilterStatus('active')} className={filterStatus === 'active' ? 'active' : ''}>Active</button>
          <button onClick={() => setFilterStatus('inactive')} className={filterStatus === 'inactive' ? 'active' : ''}>Inactive</button>
        </div>
      </div>

        {showLoading || loading ? (
        <div className="loading-container">
          <div className="skeleton-loader">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="skeleton-row">
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
          <p className="error">{error}</p>
        ) : (
          <div className="operator-list table-wrapper">
            {filteredCustomers.length > 0 ? (
              <table className="customers-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th onClick={() => setSortByExpiryAsc(prev => !prev)} style={{ cursor: 'pointer' }}>
                      Expiry {sortByExpiryAsc ? '▲' : '▼'}
                    </th>
                    <th>Operator</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((cust, index) => (
                    <tr key={cust.id} className={isActive(cust.ExpiryDate) ? 'row-active' : 'row-inactive'}>
                      <td>{index + 1}</td>
                      <td>
                        <Link to={`/customer/${cust.id}`}>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: highlightText(cust.username, searchTerm),
                            }}
                          />
                        </Link>
                      </td>

                      <td>
                        <Link to={`/customer/${cust.id}`}>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: highlightText(cust.name + " " + cust.lastName, searchTerm),
                            }}
                          />
                        </Link>
                      </td>
                      <td>{cust.MobileNumber}</td>
                      <td>{cust.ExpiryDate}</td>
                      <td>{cust.operatorDetails}</td>
                      <td>{cust.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No customers found.</p>
            )}
          </div>
        )}
    </>
  );
};

export default CustomersList;
